import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/src/renderer/components/ui/Button'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
// Bundled in the renderer's own assets (NOT resources/build, which .vercelignore
// excludes from the web build — caused the Vercel build to fail on this import).
import appIcon from '@/src/renderer/assets/images/happytask-icon-rounded.png'
import './WelcomeScreen.scss'

interface WelcomeScreenProps {
  /** Switch the setup root over to the registration/login screen
   *  (`SetupPage`). Owned by `SetupRoute`. */
  onRegister: () => void
}

/**
 * First-run welcome screen (local-first). Centered single column: app icon,
 * headline, primary "Los geht's" CTA into the offline path, and — unten — eine
 * leise Zeile für Rückkehrer.
 *
 * **Kein Konto-Angebot mehr** (08.09.2026): Hier stand eine HappyCloud-Leiste
 * mit "Mehr erfahren" und "Registrieren oder Anmelden". Am ersten Tag hat das
 * Konto nichts vorzuzeigen — Sicherung, Teilen und Web brauchen Daten, einen
 * Partner, ein zweites Gerät —, und ohne Konto zu starten kostet nichts: Ein
 * späteres Verbinden ist der normale Abgleich, keine Migration. Wer ein Konto
 * hat, sucht kein Angebot, sondern "Anmelden"; das Registrieren trägt die
 * SetupPage eine Ebene tiefer. Siehe `docs/concept/setup-konto-und-tarif.md` 3.1.
 *
 * No Supabase call happens here — going local just sets the offline storage
 * mode + loginMethod and routes to the profile setup.
 */
export function WelcomeScreen({ onRegister }: WelcomeScreenProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleStartLocal = async () => {
    const api = getPlatformAPI()
    // Default storage mode is already 'offline'; set it explicitly so the
    // local path is unambiguous and broadcasts to other windows.
    if (api.platform === 'electron') {
      try {
        await api.setStorageMode('offline')
      } catch {
        /* default is offline anyway */
      }
    }
    try {
      localStorage.setItem('loginMethod', 'offline')
    } catch (err) {
      // SecurityError in restricted contexts — navigation still works; the
      // storage mode (offline by default) remains the source of truth.
      console.error('[Welcome] Failed to persist loginMethod:', err)
    }
    navigate('/setup-profile')
  }

  /*
    ↵ startet lokal — das Plättchen am Knopf verspricht es (gemeldet
    25.09.2026). Am Fenster statt am Knopf, weil beim Öffnen nichts den Fokus
    hat. Steht der Fokus auf einem anderen Bedienelement (etwa „Anmelden"),
    gehört Enter diesem.
  */
  const startRef = useRef(handleStartLocal)
  startRef.current = handleStartLocal
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat || e.isComposing) return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const target = e.target as HTMLElement | null
      if (target?.closest?.('button, a, input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      void startRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="welcome-screen">
      <div className="welcome-screen__hero">
        <img src={appIcon} alt="" className="welcome-screen__icon" />
        <h1 className="welcome-screen__title">{t('welcome.title')}</h1>
        <div className="welcome-screen__cta">
          <Button shortcut={{ kind: 'return' }} onClick={handleStartLocal}>
            {t('welcome.startLocal')}
          </Button>
        </div>
        <p className="welcome-screen__hint">{t('welcome.localHint')}</p>
      </div>

      <div className="welcome-screen__signin">
        <p className="welcome-screen__signin-text">{t('welcome.signIn.question')}</p>
        <Button variant="outline" onClick={onRegister}>
          {t('welcome.signIn.cta')}
        </Button>
      </div>
    </div>
  )
}
